import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

export interface AssistantPanelApi {
  /** Whether the panel that answers on this screen is open (the screen's own, or the generic one). */
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

/** What a screen with its own assistant panel hands over: its open state and its toggle. */
export interface AssistantPanelRegistration { isOpen: boolean; toggle: () => void }

interface Entry extends AssistantPanelRegistration { key: object }

interface AssistantPanelInternals extends AssistantPanelApi {
  /** Open state of the generic panel (used while no screen has registered its own). */
  fallbackOpen: boolean;
  hasRegistration: boolean;
  upsert: (entry: Entry) => void;
  remove: (key: object) => void;
}

const NOOP: AssistantPanelInternals = {
  isOpen: false, toggle: () => undefined, open: () => undefined, close: () => undefined,
  fallbackOpen: false, hasRegistration: false, upsert: () => undefined, remove: () => undefined,
};
const AssistantPanelContext = createContext<AssistantPanelInternals>(NOOP);

/** Owns the one "MagicBI panel" toggle: a screen's own panel when it registered one, else the generic panel. */
export const AssistantPanelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);

  // New registrations go last (latest wins); an update keeps its place.
  const upsert = useCallback((entry: Entry) => setEntries((current) => (
    current.some((e) => e.key === entry.key) ? current.map((e) => (e.key === entry.key ? entry : e)) : [...current, entry]
  )), []);
  const remove = useCallback((key: object) => setEntries((current) => current.filter((e) => e.key !== key)), []);

  const active = entries.length > 0 ? entries[entries.length - 1] : null;
  const isOpen = active ? active.isOpen : fallbackOpen;
  const activeRef = useRef(active);
  activeRef.current = active;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const toggle = useCallback(() => {
    if (activeRef.current) activeRef.current.toggle();
    else setFallbackOpen((o) => !o);
  }, []);
  const open = useCallback(() => { if (!isOpenRef.current) toggle(); }, [toggle]);
  const close = useCallback(() => { if (isOpenRef.current) toggle(); }, [toggle]);

  const value = useMemo<AssistantPanelInternals>(
    () => ({ isOpen, toggle, open, close, fallbackOpen, hasRegistration: active !== null, upsert, remove }),
    [isOpen, toggle, open, close, fallbackOpen, active, upsert, remove],
  );
  return <AssistantPanelContext.Provider value={value}>{children}</AssistantPanelContext.Provider>;
};

export const useAssistantPanel = (): AssistantPanelApi => {
  const { isOpen, toggle, open, close } = useContext(AssistantPanelContext);
  return useMemo(() => ({ isOpen, toggle, open, close }), [isOpen, toggle, open, close]);
};

/** For the generic panel only: should it show (no screen panel registered, and toggled on)? */
export const useGenericAssistantPanel = (): { visible: boolean; close: () => void } => {
  const { fallbackOpen, hasRegistration, close } = useContext(AssistantPanelContext);
  return { visible: fallbackOpen && !hasRegistration, close };
};

/**
 * A screen with its own assistant panel calls this with its open state and toggle. The latest mounted registration wins;
 * it is removed on unmount, which restores the generic panel.
 */
export const useRegisterAssistantPanel = ({ isOpen, toggle }: AssistantPanelRegistration): void => {
  const { upsert, remove } = useContext(AssistantPanelContext);
  const key = useRef<object>({}).current;
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;
  const stableToggle = useCallback(() => toggleRef.current(), []);
  useEffect(() => { upsert({ key, isOpen, toggle: stableToggle }); }, [upsert, key, isOpen, stableToggle]);
  useEffect(() => () => remove(key), [remove, key]);
};
